import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TaskCounterService, ActiveTask } from './task-counter.service';
import { getTaskUrl } from './task-navigation.util';
import { PlanRefreshService } from './plan-refresh.service';
import { PlanReviewerService } from './plan-reviewer.service';
import { Subscription } from 'rxjs';
import { filter, map, pairwise } from 'rxjs/operators';

/**
 * Global service for showing task completion notifications across the entire application.
 * Shows snackbar notifications when tasks complete, regardless of which page the user is on,
 * as long as they're within the same architectural project.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalTaskNotificationService {
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private taskCounterService = inject(TaskCounterService);
  private planRefreshService = inject(PlanRefreshService);
  private planReviewerService = inject(PlanReviewerService);

  private taskSubscription?: Subscription;
  private currentProjectId?: string;

  // Issue #1603: a large parallel fan-out can complete dozens/hundreds of tasks within the same
  // few seconds. TaskCounterService emits one activeTasks$ update per task completion, so without
  // batching this service would show one stacked snackbar and trigger one plan-refresh cascade
  // per task — flooding the UI and eventually starving the browser's connection pool. Buffer
  // newly-completed tasks for a short window and process them together as a single batch.
  private static readonly COMPLETION_BATCH_WINDOW_MS = 1000;
  private pendingCompletedTasks: ActiveTask[] = [];
  private pendingBatchProjectId: string | null = null;
  private batchFlushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    ClientLoggingService.log('INFO', '[GlobalTaskNotificationService] Initialized');
  }

  /**
   * Start listening for task completions within a specific project
   */
  startListening(projectId: string): void {
    if (this.currentProjectId === projectId && this.taskSubscription) {
      ClientLoggingService.log('INFO', `[GlobalTaskNotificationService] Already listening for project: ${projectId}`);
      return;
    }

    // Stop any existing subscription
    this.stopListening();

    this.currentProjectId = projectId;
    ClientLoggingService.log(
      'INFO',
      `[GlobalTaskNotificationService] Starting to listen for task completions in project: ${projectId}`
    );

    // Listen to active tasks changes and detect completions
    this.taskSubscription = this.taskCounterService.activeTasks$
      .pipe(
        // Use pairwise to compare previous and current task states
        pairwise(),
        map(([previousTasks, currentTasks]: [ActiveTask[], ActiveTask[]]) =>
          this.findNewlyCompletedTasks(previousTasks, currentTasks)
        ),
        // Filter for relevant changes (task completions)
        filter(newlyCompletedTasks => newlyCompletedTasks.length > 0)
      )
      .subscribe(newlyCompletedTasks => {
        this.queueTaskCompletions(newlyCompletedTasks, projectId);
      });
  }

  /**
   * Stop listening for task completions
   */
  stopListening(): void {
    if (this.taskSubscription) {
      ClientLoggingService.log(
        'INFO',
        `[GlobalTaskNotificationService] Stopping task completion listener for project: ${this.currentProjectId}`
      );
      this.taskSubscription.unsubscribe();
      this.taskSubscription = undefined;
    }
    this.currentProjectId = undefined;

    // Drop any pending batch rather than flushing it against a project we're no longer watching.
    if (this.batchFlushTimer) {
      clearTimeout(this.batchFlushTimer);
      this.batchFlushTimer = null;
    }
    this.pendingCompletedTasks = [];
    this.pendingBatchProjectId = null;
  }

  /**
   * Find tasks that were not complete before but are complete now. Indexes `previousTasks` by
   * taskId first so this is O(n) rather than O(n^2) — this runs on every activeTasks$ emission,
   * and during a large fan-out both task lists can be in the hundreds (Issue #1603).
   */
  private findNewlyCompletedTasks(previousTasks: ActiveTask[], currentTasks: ActiveTask[]): ActiveTask[] {
    const previousTasksById = new Map(previousTasks.map(task => [task.taskId, task]));
    const newlyCompletedTasks: ActiveTask[] = [];
    for (const currentTask of currentTasks) {
      if (currentTask.status === 'complete' && currentTask.endTime) {
        const previousTask = previousTasksById.get(currentTask.taskId);
        if (previousTask && previousTask.status !== 'complete') {
          newlyCompletedTasks.push(currentTask);
        }
      }
    }
    return newlyCompletedTasks;
  }

  /**
   * Buffer newly-completed tasks for COMPLETION_BATCH_WINDOW_MS before processing them as a
   * single batch (Issue #1603), instead of showing a notification and triggering a plan refresh
   * per individual task completion.
   */
  private queueTaskCompletions(newlyCompletedTasks: ActiveTask[], projectId: string): void {
    this.pendingCompletedTasks.push(...newlyCompletedTasks);
    this.pendingBatchProjectId = projectId;

    if (!this.batchFlushTimer) {
      this.batchFlushTimer = setTimeout(
        () => this.flushPendingCompletions(),
        GlobalTaskNotificationService.COMPLETION_BATCH_WINDOW_MS
      );
    }
  }

  /**
   * Process the buffered batch of completed tasks: show one notification (individual detail for
   * a single completion, a summary for a batch) and refresh UI data once for the whole batch.
   */
  private flushPendingCompletions(): void {
    const tasks = this.pendingCompletedTasks;
    const projectId = this.pendingBatchProjectId;
    this.pendingCompletedTasks = [];
    this.pendingBatchProjectId = null;
    this.batchFlushTimer = null;

    if (tasks.length === 0 || !projectId) {
      return;
    }

    ClientLoggingService.log(
      'INFO',
      `[GlobalTaskNotificationService] Flushing batch of ${tasks.length} completed task(s) for project ${projectId}`
    );

    this.showTaskCompletionNotification(tasks, projectId);
    this.refreshUIDataForCompletedTasks(tasks, projectId);
  }

  /**
   * Show a snackbar notification for a batch of completed tasks: the original detailed message
   * for a single completion, or a coalesced summary when several completed in the same window.
   */
  private showTaskCompletionNotification(tasks: ActiveTask[], projectId: string): void {
    if (tasks.length === 1) {
      this.showSingleTaskCompletionNotification(tasks[0], projectId);
      return;
    }
    this.showBatchTaskCompletionNotification(tasks, projectId);
  }

  private showSingleTaskCompletionNotification(task: ActiveTask, projectId: string): void {
    const taskType = this.getTaskType(task);
    const pageInfo = this.extractPageInfo(task);

    let message: string;
    if (taskType === 'code-applicability') {
      message = pageInfo
        ? `${pageInfo} code applicability analysis completed`
        : `Code applicability analysis completed: ${task.filename}`;
    } else if (taskType === 'compliance-report') {
      message = pageInfo ? `${pageInfo} compliance report completed` : `Compliance report completed: ${task.filename}`;
    } else {
      message = pageInfo ? `${pageInfo} ingestion completed` : `Task completed: ${task.filename}`;
    }

    ClientLoggingService.log('INFO', `[GlobalTaskNotificationService] Showing completion notification: ${message}`);

    // Show snackbar with "View Task" action to open the completed task
    const snackBarRef = this.snackBar.open(message, 'View Task', {
      duration: 8000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['task-completion-snackbar'],
    });

    snackBarRef.onAction().subscribe(() => {
      ClientLoggingService.log(
        'INFO',
        `[GlobalTaskNotificationService] User clicked "View Task" - navigating to task ${task.taskId}`
      );
      this.router.navigate(getTaskUrl(projectId, task.taskId));
    });
  }

  /**
   * Show a single coalesced snackbar summarizing a batch of completions (Issue #1603), instead of
   * stacking/cycling one snackbar per task when a large fan-out finishes many tasks at once.
   */
  private showBatchTaskCompletionNotification(tasks: ActiveTask[], projectId: string): void {
    const message = `${tasks.length} tasks completed`;
    ClientLoggingService.log(
      'INFO',
      `[GlobalTaskNotificationService] Showing batched completion notification: ${message}`
    );

    const snackBarRef = this.snackBar.open(message, 'View Tasks', {
      duration: 8000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['task-completion-snackbar'],
    });

    snackBarRef.onAction().subscribe(() => {
      ClientLoggingService.log(
        'INFO',
        `[GlobalTaskNotificationService] User clicked "View Tasks" - navigating to task list for project ${projectId}`
      );
      this.router.navigate(getTaskUrl(projectId));
    });
  }

  /**
   * Extract page information from task description
   */
  private extractPageInfo(task: ActiveTask): string | null {
    try {
      // Try to extract page number from task filename
      // Expected format: "filename.pdf (Page X)" or similar
      const pageMatch = task.filename.match(/\(Page\s+(\d+)\)/i);
      if (pageMatch) {
        return `Page ${pageMatch[1]}`;
      }

      // Try to extract from task message if available
      if (task.message) {
        const messagePageMatch = task.message.match(/page\s+(\d+)/i);
        if (messagePageMatch) {
          return `Page ${messagePageMatch[1]}`;
        }
      }

      // Fallback: try to extract filename without extension
      const filenameMatch = task.filename.match(/^([^(]+?)(?:\s*\(|$)/);
      if (filenameMatch) {
        let filename = filenameMatch[1].trim();
        // Remove file extension if present
        filename = filename.replace(/\.[^.]*$/, '');
        return filename;
      }

      return null;
    } catch (error) {
      ClientLoggingService.log('WARN', '[GlobalTaskNotificationService] Error extracting page info:', error);
      return null;
    }
  }

  /**
   * Refresh UI data model for a batch of completed tasks (Issue #1603). Clears the plan data
   * cache once for the whole batch and issues at most one refresh call per refresh "kind"
   * (general plan refresh, and one refreshComplianceData call per distinct page), instead of
   * repeating the same cache-clear/refresh cascade once per individual task completion.
   */
  private refreshUIDataForCompletedTasks(tasks: ActiveTask[], projectId: string): void {
    ClientLoggingService.log(
      'INFO',
      `[GlobalTaskNotificationService] Refreshing UI data for a batch of ${tasks.length} completed task(s) (project: ${projectId})`
    );

    // Clear relevant caches once for the whole batch
    this.planReviewerService.clearPlanDataCache();

    const pageIngestionPages = new Set<number>();
    const complianceRefreshes = new Map<number, string>(); // pageNumber -> reason
    let needsFullPlanRefresh = false;

    for (const task of tasks) {
      const pageNumber = this.extractPageNumber(task);
      if (pageNumber === null) {
        // If we can't determine the page number, fall back to refreshing the entire plan.
        needsFullPlanRefresh = true;
        continue;
      }

      switch (this.getTaskType(task)) {
        case 'code-applicability':
          complianceRefreshes.set(pageNumber, 'Code applicability analysis completed');
          break;
        case 'compliance-report':
          complianceRefreshes.set(pageNumber, 'Compliance report completed');
          break;
        case 'page-ingestion':
        default:
          pageIngestionPages.add(pageNumber);
          break;
      }
    }

    // A full plan refresh already covers every page-ingestion page, so only issue the
    // page-scoped refresh when a full refresh isn't already happening for this batch — otherwise
    // both calls end up going through PlanRefreshService.refreshPlan() and, despite the
    // coalescing there, still produce two separate emissions (an immediate one plus a trailing
    // one) for what should be a single batch refresh.
    if (needsFullPlanRefresh) {
      const reason = tasks.length === 1 ? `Task completed: ${tasks[0].filename}` : `${tasks.length} tasks completed`;
      ClientLoggingService.log(
        'INFO',
        `[GlobalTaskNotificationService] 🔄 Refreshing entire plan ${projectId} after task completion(s) (page number unknown)`
      );
      this.planRefreshService.refreshPlan(projectId, reason);
    } else if (pageIngestionPages.size > 0) {
      ClientLoggingService.log(
        'INFO',
        `[GlobalTaskNotificationService] 🔄 Refreshing table of contents for plan ${projectId} after completing pages: ${[...pageIngestionPages].join(', ')}`
      );
      this.planRefreshService.refreshPlanPages(projectId, [...pageIngestionPages]);
    }

    for (const [pageNumber, reason] of complianceRefreshes) {
      ClientLoggingService.log(
        'INFO',
        `[GlobalTaskNotificationService] 🔄 Refreshing compliance data for plan ${projectId} page ${pageNumber}: ${reason}`
      );
      this.planRefreshService.refreshComplianceData(projectId, pageNumber, reason);
    }
  }

  /**
   * Determine the task type based on task properties
   */
  private getTaskType(task: ActiveTask): 'page-ingestion' | 'code-applicability' | 'compliance-report' | 'unknown' {
    // Check task filename or message for indicators
    const filename = task.filename?.toLowerCase() || '';
    const message = task.message?.toLowerCase() || '';

    // Look for code applicability indicators
    if (
      filename.includes('code-applicability') ||
      message.includes('code applicability') ||
      message.includes('applicable sections') ||
      message.includes('applicability analysis')
    ) {
      return 'code-applicability';
    }

    // Look for compliance report indicators
    if (
      filename.includes('compliance-report') ||
      message.includes('compliance report') ||
      message.includes('compliance analysis')
    ) {
      return 'compliance-report';
    }

    // Look for page ingestion indicators
    if (
      filename.includes('page-ingestion') ||
      message.includes('page ingestion') ||
      message.includes('pdf processing') ||
      message.includes('page processing')
    ) {
      return 'page-ingestion';
    }

    // Default to page ingestion if no specific indicators found
    // This maintains backward compatibility with existing tasks
    return 'page-ingestion';
  }

  /**
   * Extract page number from task information
   */
  private extractPageNumber(task: ActiveTask): number | null {
    try {
      // Try to extract page number from task filename
      const pageMatch = task.filename.match(/\(Page\s+(\d+)\)/i) || task.filename.match(/page\s+(\d+)/i);
      if (pageMatch) {
        return parseInt(pageMatch[1], 10);
      }

      // Try to extract from task message if available
      if (task.message) {
        const messagePageMatch = task.message.match(/page\s+(\d+)/i);
        if (messagePageMatch) {
          return parseInt(messagePageMatch[1], 10);
        }
      }

      return null;
    } catch (error) {
      ClientLoggingService.log('WARN', '[GlobalTaskNotificationService] Error extracting page number:', error);
      return null;
    }
  }

  /**
   * Get the current project ID being monitored
   */
  getCurrentProjectId(): string | undefined {
    return this.currentProjectId;
  }
}
